import { useEffect } from 'react';

import { Button } from '@gearbelt/ui/components';
import { useAuth } from 'react-oidc-context';
import { useLocation, useNavigate } from 'react-router-dom';

import manholeShrugVector from '@/assets/vectors/manhole-shrug.svg';
import LogoutIcon from '@/components/icons/logout';
import { useUserStore } from '@/stores/user.store';

/**
 * Navigation state reason for reaching this page because a caller (e.g. the
 * workspace-setup cross-account flow) detected that the user isn't authorized
 * for the operation they were trying to perform. When this state is present we
 * suppress the `isNewUser → /workspace-setup` bounce and show a contextual
 * access-denied message instead, so a brand-new user doesn't get looped back
 * into a form they can't complete.
 */
export const CROSS_ACCOUNT_DENIED_REASON = 'cross-account-denied';

type NoWorkspaceAvailableLocationState = {
  reason?: typeof CROSS_ACCOUNT_DENIED_REASON;
};

const NoWorkspaceAvailable = () => {
  const { setUser, setOrg, org, isNewUser } = useUserStore();
  const { signoutRedirect } = useAuth();
  // Plain `useNavigate` (not `useWorkspaceNavigate`) because both destinations are top-level
  // routes: `/dashboard` itself redirects to `/workspaces/:id/dashboard`, and `/workspace-setup`
  // must NOT be prefixed with the workspace id (it's not a workspace-nested route).
  const navigate = useNavigate();
  const location = useLocation();
  const isAccessDenied = (location.state as NoWorkspaceAvailableLocationState | null)?.reason === CROSS_ACCOUNT_DENIED_REASON;

  useEffect(() => {
    if (org) {
      navigate('/dashboard');
    } else if (isNewUser && !isAccessDenied) {
      // Default behaviour: bounce new users into the workspace-setup flow so they can create
      // their first workspace. Skipped when we arrived here via an explicit access denial.
      navigate('/workspace-setup');
    }
  }, [org, navigate, isNewUser, isAccessDenied]);

  const heading = isAccessDenied ? "You don't have access to that account" : 'No workspace available';
  const body = isAccessDenied
    ? "You don't have access to create a Gearbelt workspace for the account you were referred to. Please contact the person who invited you, or log out to sign in with a different account."
    : 'You don’t have access to any workspaces. Please contact your admin to gain access.';

  return (
    <div className="bg-grey-1 flex h-full flex-col items-center">
      <div className="mt-25 flex w-[500px] flex-col items-center justify-center gap-4 rounded bg-white p-30">
        <div className="flex flex-col items-center gap-4">
          <img src={manholeShrugVector} alt="Manhole shrug" className="h-[100px] w-[200px]" />
          <h1 className="text-xl font-bold">{heading}</h1>
        </div>

        <p className="text-default-text text-center text-sm">{body}</p>

        <Button
          variant="grey"
          icon={<LogoutIcon />}
          onClick={async () => {
            setUser(null);
            setOrg(null);
            await signoutRedirect({
              post_logout_redirect_uri: window.location.origin,
            });
          }}>
          Logout
        </Button>
      </div>
    </div>
  );
};

export default NoWorkspaceAvailable;
